import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';

import { toCoreApiMutationResult } from '@/utils/core-api-mutation-result';
import { serverFetch } from '@/utils/server-fetch';

import type { SettingsMutationResult } from './settings-access.server';

import { can } from './permissions';
import {
  guardedMutation,
  loadSettingsActor,
  requireSettingsPermission,
  SettingsAccessError,
} from './settings-access.server';

/**
 * Team settings: the active team's details, and member management for any
 * team in the active organization (Browse teams manages others). Ported from
 * the legacy team settings and "Manage members" pages, which called these
 * same core-api routes; the permission checks are new (see
 * `settings-access.server.ts`).
 */

const objectId = z.string().regex(/^[a-f\d]{24}$/i, 'Invalid id');

export type TeamRole = 'owner' | 'admin' | 'member';

function roleFrom(roles: readonly string[] | null | undefined): TeamRole {
  if (roles?.includes('owner')) return 'owner';
  if (roles?.includes('admin')) return 'admin';
  return 'member';
}

/** The role arrays legacy wrote for each team role. */
const ROLE_RECORDS: Record<TeamRole, string[]> = {
  owner: ['owner', 'member'],
  admin: ['admin', 'member'],
  member: ['member'],
};

const teamPath = (organizationId: string, teamId: string) =>
  `/organizations/${encodeURIComponent(organizationId)}/teams/${encodeURIComponent(teamId)}`;

// Team details

export interface TeamDetails {
  organizationId: string;
  teamId: string;
  name: string;
  description: string;
  imageUrl?: string;
  canEdit: boolean;
  /** core-api lets only the team's owner and admins upload its picture. */
  canUploadPicture: boolean;
  /** Why the team cannot be deleted, when the researcher otherwise could. */
  deleteBlockedReason?: 'last-team' | 'default-team';
  canDelete: boolean;
}

const rawTeamListSchema = z.array(
  z.object({
    id: z.string().optional(),
    _id: z.string().optional(),
    createdAt: z.string().nullish(),
  }),
);

export const getTeamDetails = createServerFn().handler(
  async (): Promise<TeamDetails> => {
    const actor = await loadSettingsActor();
    if (!actor.team || !actor.teamId) {
      throw new SettingsAccessError('not-found', 'No active team');
    }
    const team = actor.team;

    // Legacy refused to delete the organization's last team, or its default
    // team (the earliest created), before asking core-api. Core-api itself
    // only lets the team owner delete, so that is required too.
    let deleteBlockedReason: TeamDetails['deleteBlockedReason'];
    const mayDelete = can('deleteTeam', actor.roles) && actor.roles.teamOwner;
    if (mayDelete) {
      const response = await serverFetch({
        path: `/v4/organizations/${encodeURIComponent(actor.organizationId)}/teams`,
        userId: actor.userId,
      });
      if (response.ok) {
        const teams = rawTeamListSchema
          .parse(await response.json())
          .map((t) => ({ id: t.id ?? t._id, createdAt: t.createdAt ?? '' }));
        if (teams.length <= 1) {
          deleteBlockedReason = 'last-team';
        } else {
          const earliest = teams.toSorted((a, b) =>
            a.createdAt.localeCompare(b.createdAt),
          )[0];
          if (earliest?.id === actor.teamId) {
            deleteBlockedReason = 'default-team';
          }
        }
      }
    }

    return {
      organizationId: actor.organizationId,
      teamId: actor.teamId,
      name: team.name ?? '',
      description: team.description ?? '',
      imageUrl: team.imageUrl || undefined,
      canEdit: can('editTeamDetails', actor.roles),
      canUploadPicture: actor.roles.teamOwner || actor.roles.teamAdmin,
      canDelete: mayDelete && !deleteBlockedReason,
      ...(mayDelete && deleteBlockedReason ? { deleteBlockedReason } : {}),
    };
  },
);

export const teamDetailsSchema = z.object({
  name: z.string().trim().min(1).max(100),
  description: z.string().trim().max(1000),
});

export const updateTeamDetails = createServerFn({ method: 'POST' })
  .validator((data: z.infer<typeof teamDetailsSchema>) =>
    teamDetailsSchema.parse(data),
  )
  .handler(({ data }) =>
    guardedMutation('updateTeamDetails', async () => {
      const actor = await requireSettingsPermission('editTeamDetails');
      if (!actor.teamId) return { success: false };
      const response = await serverFetch({
        path: teamPath(actor.organizationId, actor.teamId),
        method: 'PUT',
        userId: actor.userId,
        body: JSON.stringify(data),
      });
      return toCoreApiMutationResult(response, 'updateTeamDetails');
    }),
  );

export const deleteActiveTeam = createServerFn({ method: 'POST' }).handler(() =>
  guardedMutation('deleteActiveTeam', async () => {
    const details = await getTeamDetails();
    if (!details.canDelete) {
      return {
        success: false,
        message:
          details.deleteBlockedReason === 'last-team'
            ? 'An organization needs at least one team.'
            : details.deleteBlockedReason === 'default-team'
              ? "The organization's first team can't be deleted."
              : 'Only the team owner can delete this team.',
      };
    }
    const actor = await loadSettingsActor();
    if (!actor.teamId) return { success: false };
    const response = await serverFetch({
      path: teamPath(actor.organizationId, actor.teamId),
      method: 'DELETE',
      userId: actor.userId,
    });
    return toCoreApiMutationResult(response, 'deleteActiveTeam');
  }),
);

// Team members

export interface TeamMemberRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  picture?: string;
  role: TeamRole;
  lastLoginAt?: string;
}

export interface TeamMembers {
  teamId: string;
  teamName: string;
  currentUserId: string;
  members: TeamMemberRow[];
  can: {
    add: boolean;
    changeRole: boolean;
    transferOwnership: boolean;
    remove: boolean;
  };
}

const rawTeamMemberSchema = z.object({
  userId: z.string(),
  roles: z.array(z.string()).nullish(),
  isActive: z.boolean().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  email: z.string().nullish(),
  picture: z.string().nullish(),
});

const rawOrgMemberSchema = z.object({
  id: z.string(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  email: z.string().nullish(),
  picture: z.string().nullish(),
  lastLoginAt: z.string().nullish(),
});

async function fetchList<T>(
  path: string,
  userId: string,
  schema: z.ZodType<T>,
): Promise<T[]> {
  const response = await serverFetch({ path, userId });
  if (!response.ok) {
    throw new Error(`core-api ${path} responded ${response.status}`);
  }
  const json: unknown = await response.json();
  return (Array.isArray(json) ? json : []).flatMap((item) => {
    const parsed = schema.safeParse(item);
    return parsed.success ? [parsed.data] : [];
  });
}

const teamIdInput = z.object({ teamId: objectId.optional() });

export const getTeamMembers = createServerFn()
  .validator((data: z.infer<typeof teamIdInput>) => teamIdInput.parse(data))
  .handler(async ({ data }): Promise<TeamMembers> => {
    const actor = await loadSettingsActor({ teamId: data.teamId });
    if (!actor.teamId) {
      throw new SettingsAccessError('not-found', 'No active team');
    }
    // With private teams, only organization owners and admins see the teams
    // they are not on (core-api's team search applies the same rule).
    const { orgOwner, orgAdmin, teamMember } = actor.roles;
    if (
      actor.organization.privateTeams &&
      !orgOwner &&
      !orgAdmin &&
      !teamMember
    ) {
      throw new SettingsAccessError('forbidden', 'Team not found');
    }
    const [teamMembers, orgMembers] = await Promise.all([
      fetchList(
        `${teamPath(actor.organizationId, actor.teamId)}/members`,
        actor.userId,
        rawTeamMemberSchema,
      ),
      fetchList(
        `/organizations/${encodeURIComponent(actor.organizationId)}/members`,
        actor.userId,
        rawOrgMemberSchema,
      ),
    ]);
    const lastLogin = new Map(
      orgMembers.map((m) => [m.id, m.lastLoginAt ?? undefined]),
    );

    return {
      teamId: actor.teamId,
      teamName: actor.team?.name ?? '',
      currentUserId: actor.userId,
      members: teamMembers
        .filter((m) => m.isActive !== false)
        .map((m) => ({
          id: m.userId,
          firstName: m.firstName ?? '',
          lastName: m.lastName ?? '',
          email: m.email ?? '',
          picture: m.picture || undefined,
          role: roleFrom(m.roles),
          lastLoginAt: lastLogin.get(m.userId) || undefined,
        }))
        .toSorted((a, b) =>
          `${a.firstName} ${a.lastName}`.localeCompare(
            `${b.firstName} ${b.lastName}`,
          ),
        ),
      can: {
        add: can('addToTeam', actor.roles),
        changeRole: can('changeRoleTeam', actor.roles),
        transferOwnership: can('transferTeamOwnership', actor.roles),
        remove: can('removeFromTeam', actor.roles),
      },
    };
  });

export interface AddableMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  picture?: string;
}

/** Organization members who are not on the team yet. */
export const getAddableTeamMembers = createServerFn()
  .validator((data: z.infer<typeof teamIdInput>) => teamIdInput.parse(data))
  .handler(async ({ data }): Promise<AddableMember[]> => {
    const actor = await requireSettingsPermission('addToTeam', {
      teamId: data.teamId,
    });
    if (!actor.teamId) return [];
    const onTeam = new Set(
      (actor.team?.members ?? [])
        .filter((m) => m.isActive !== false)
        .map((m) => m.userId),
    );
    const orgMembers = await fetchList(
      `/organizations/${encodeURIComponent(actor.organizationId)}/members`,
      actor.userId,
      rawOrgMemberSchema,
    );
    return orgMembers
      .filter((m) => !onTeam.has(m.id))
      .map((m) => ({
        id: m.id,
        firstName: m.firstName ?? '',
        lastName: m.lastName ?? '',
        email: m.email ?? '',
        picture: m.picture || undefined,
      }))
      .toSorted((a, b) =>
        `${a.firstName} ${a.lastName}`.localeCompare(
          `${b.firstName} ${b.lastName}`,
        ),
      );
  });

const memberInput = z.object({ teamId: objectId, userId: objectId });

export const addTeamMember = createServerFn({ method: 'POST' })
  .validator((data: z.infer<typeof memberInput>) => memberInput.parse(data))
  .handler(({ data }) =>
    guardedMutation('addTeamMember', async () => {
      const actor = await requireSettingsPermission('addToTeam', {
        teamId: data.teamId,
      });
      const response = await serverFetch({
        path: `${teamPath(actor.organizationId, data.teamId)}/members`,
        method: 'POST',
        userId: actor.userId,
        body: JSON.stringify({ userId: data.userId }),
      });
      return toCoreApiMutationResult(response, 'addTeamMember');
    }),
  );

async function writeRole(
  organizationId: string,
  teamId: string,
  actorId: string,
  userId: string,
  role: TeamRole,
): Promise<SettingsMutationResult> {
  const response = await serverFetch({
    path: `${teamPath(organizationId, teamId)}/members/${encodeURIComponent(userId)}`,
    method: 'PUT',
    userId: actorId,
    body: JSON.stringify({ roles: ROLE_RECORDS[role], isActive: true }),
  });
  return toCoreApiMutationResult(response, 'writeTeamRole');
}

function roleOnTeam(
  members: readonly { userId: string; roles?: readonly string[] | null }[],
  userId: string,
): TeamRole | undefined {
  const member = members.find((m) => m.userId === userId);
  return member ? roleFrom(member.roles) : undefined;
}

const changeRoleInput = memberInput.extend({
  role: z.enum(['admin', 'member']),
});

export const changeTeamMemberRole = createServerFn({ method: 'POST' })
  .validator((data: z.infer<typeof changeRoleInput>) =>
    changeRoleInput.parse(data),
  )
  .handler(({ data }) =>
    guardedMutation('changeTeamMemberRole', async () => {
      const actor = await requireSettingsPermission('changeRoleTeam', {
        teamId: data.teamId,
      });
      const current = roleOnTeam(actor.team?.members ?? [], data.userId);
      if (!current) return { success: false, message: 'Not on this team.' };
      // Ownership moves only through a transfer, and nobody changes their own
      // role, as in legacy.
      if (current === 'owner') {
        return {
          success: false,
          message: 'Transfer ownership to change the owner.',
        };
      }
      if (data.userId === actor.userId) {
        return { success: false, message: "You can't change your own role." };
      }
      if (current === data.role) return { success: true };
      return writeRole(
        actor.organizationId,
        data.teamId,
        actor.userId,
        data.userId,
        data.role,
      );
    }),
  );

export const transferTeamOwnership = createServerFn({ method: 'POST' })
  .validator((data: z.infer<typeof memberInput>) => memberInput.parse(data))
  .handler(({ data }) =>
    guardedMutation('transferTeamOwnership', async () => {
      const actor = await requireSettingsPermission('transferTeamOwnership', {
        teamId: data.teamId,
      });
      const members = actor.team?.members ?? [];
      const current = roleOnTeam(members, data.userId);
      if (!current) return { success: false, message: 'Not on this team.' };
      if (current === 'owner') return { success: true };

      const promoted = await writeRole(
        actor.organizationId,
        data.teamId,
        actor.userId,
        data.userId,
        'owner',
      );
      if (!promoted.success) return promoted;

      // Every previous owner becomes an admin. Some teams have more than one
      // owner from old data; legacy demoted only the first it found.
      for (const member of members) {
        if (member.userId === data.userId) continue;
        if (!member.roles?.includes('owner')) continue;
        // One at a time, so a failure stops before the next owner changes.
        // eslint-disable-next-line no-await-in-loop
        const demoted = await writeRole(
          actor.organizationId,
          data.teamId,
          actor.userId,
          member.userId,
          'admin',
        );
        if (!demoted.success) return demoted;
      }
      return { success: true };
    }),
  );

export const removeTeamMember = createServerFn({ method: 'POST' })
  .validator((data: z.infer<typeof memberInput>) => memberInput.parse(data))
  .handler(({ data }) =>
    guardedMutation('removeTeamMember', async () => {
      const actor = await requireSettingsPermission('removeFromTeam', {
        teamId: data.teamId,
      });
      const current = roleOnTeam(actor.team?.members ?? [], data.userId);
      if (!current) return { success: true };
      if (current === 'owner') {
        return {
          success: false,
          message: 'Transfer ownership before removing the owner.',
        };
      }
      if (data.userId === actor.userId) {
        return {
          success: false,
          message: "You can't remove yourself from the team.",
        };
      }
      const response = await serverFetch({
        path: `${teamPath(actor.organizationId, data.teamId)}/members/${encodeURIComponent(data.userId)}`,
        method: 'DELETE',
        userId: actor.userId,
      });
      return toCoreApiMutationResult(response, 'removeTeamMember');
    }),
  );
